import React from 'react';

import {
  Box,
  VStack,
  Flex,
  HStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  InputLeftAddon,
  Select,
  Button,
  IconButton,
} from '@chakra-ui/react';

import { SearchIcon } from '@chakra-ui/icons';
import { BsFilter } from 'react-icons/bs';

import { useColors } from './colors';

const FilterSection = ({
  searchField,
  setSearchField,
  searchQuery,
  setSearchQuery,
  handleSearch,
  filters,
  handleFilterChange,
  showFilters,
  setShowFilters,
  marketplaceOptions,
  storeOptions,
  supplierOptions,
  getMarketplacesListStatus,
  getStoresListStatus,
  getSuppliersListStatus,
  pageSize,
  handlePageSizeChange,
  currentPage,
  totalListings,
  listings,
}) => {
  const {
    borderColor,
    inputColor,
    placeholderColor,
    filterSectionBg,
    filterBg,
    filterOptionHover,
    filterOptionChecked,
    filterHover,
    filterChecked,
  } = useColors();

  const selectStyles = {
    option: {
      bg: filterBg,
      color: inputColor,
    },
    '> option:hover': {
      bg: filterOptionHover + '!important',
    },
    '> option:checked': {
      bg: filterOptionChecked + '!important',
    },
  };

  const selectProps = {
    sx: selectStyles,
    _hover: {
      bg: filterHover,
    },
    _focus: {
      bg: filterChecked,
      borderColor: 'blue.300',
    },
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Count and Controls */}
      <Flex justify="space-between" align="center">
        <HStack spacing={4}>
          <Text fontSize="sm">
            {listings?.length > 0
              ? `${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, totalListings)} of ${totalListings}`
              : '0 - 0 of 0'}
          </Text>
        </HStack>

        <Select
          size="sm"
          value={pageSize}
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          w="150px"
          {...selectProps}
        >
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
          <option value={200}>200 per page</option>
        </Select>
      </Flex>

      <Flex gap={4} flexWrap="wrap">
        <Box flex={2} minW="300px">
          <InputGroup width="100%" maxW="600px">
            <InputLeftAddon p={0} borderRightRadius={0} bg={filterSectionBg}>
              <Select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                h="100%"
                border="none"
                {...selectProps}
              >
                <option value="all">All</option>
                <option value="sku">SKU</option>
                <option value="marketplace-sku">Item ID / ASIN</option>
                <option value="title">Title</option>
              </Select>
            </InputLeftAddon>
            <Input
              color={inputColor}
              placeholder={`Search ${searchField === 'all' ? '' : searchField.toUpperCase()}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              borderLeftRadius={0}
            />
            <InputRightElement>
              <IconButton
                icon={<SearchIcon />}
                aria-label="Search"
                size="sm"
                onClick={handleSearch}
              />
            </InputRightElement>
          </InputGroup>
        </Box>
        <HStack spacing={4} flex={1} justify="flex-end">
          <Select
            width="200px"
            placeholder="Status"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            {...selectProps}
          >
            <option value="">All</option>
            <option value="optimized">Optimized</option>
            <option value="active">Active</option>
            <option value="queue">Queue</option>
            <option value="hold">Hold</option>
            <option value="not-optimized">Not Optimized</option>
          </Select>
          <Button
            leftIcon={<BsFilter />}
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            colorScheme={showFilters ? 'blue' : 'gray'}
          >
            All filters
          </Button>
        </HStack>
      </Flex>

      {/* Advanced Filters */}
      {showFilters && (
        <Box
          p={4}
          py={8}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg={filterSectionBg}
        >
          <HStack spacing={8} align="start">
            {/* Price Range Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Current Price Range
              </Text>
              <HStack spacing={2}>
                <InputGroup size="sm" width="120px">
                  <InputLeftAddon children="$" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={filters.priceMin}
                    onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
                <Text>to</Text>
                <InputGroup size="sm" width="120px">
                  <InputLeftAddon children="$" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={filters.priceMax}
                    onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
              </HStack>
            </Box>

            {/* Competitor Price Range Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Competitor Price Range
              </Text>
              <HStack spacing={2}>
                <InputGroup size="sm" width="120px">
                  <InputLeftAddon children="$" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={filters.competitorPriceMin}
                    onChange={(e) =>
                      handleFilterChange('competitorPriceMin', e.target.value)
                    }
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
                <Text>to</Text>
                <InputGroup size="sm" width="120px">
                  <InputLeftAddon children="$" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={filters.competitorPriceMax}
                    onChange={(e) =>
                      handleFilterChange('competitorPriceMax', e.target.value)
                    }
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
              </HStack>
            </Box>

            {/* Marketplace Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Marketplace
              </Text>
              <Select
                size="sm"
                width="200px"
                placeholder="Select marketplace"
                value={filters.marketplace}
                onChange={(e) => handleFilterChange('marketplace', e.target.value)}
                isDisabled={getMarketplacesListStatus === 'pending'}
                {...selectProps}
              >
                <option value="">All Marketplaces</option>
                {marketplaceOptions?.map((option) => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Store Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Store
              </Text>
              <Select
                size="sm"
                width="200px"
                placeholder="Select store"
                value={filters.store}
                onChange={(e) => handleFilterChange('store', e.target.value)}
                isDisabled={getStoresListStatus === 'pending'}
                {...selectProps}
              >
                <option value="">All Stores</option>
                {storeOptions?.map((option) => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Supplier Filter */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Supplier
              </Text>
              <Select
                size="sm"
                width="200px"
                placeholder="Select supplier"
                value={filters.supplier}
                onChange={(e) => handleFilterChange('supplier', e.target.value)}
                isDisabled={getSuppliersListStatus === 'pending'}
                {...selectProps}
              >
                <option value="">All Suppliers</option>
                <option value="no-supplier">No Supplier</option>
                {supplierOptions?.map((option) => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Clear Filters Button */}
            <Button
              size="sm"
              variant="ghost"
              colorScheme="blue"
              onClick={() =>
                handleFilterChange('clear', {
                  priceMin: '',
                  priceMax: '',
                  competitorPriceMin: '',
                  competitorPriceMax: '',
                  marketplace: '',
                  supplier: '',
                  store: '',
                  status: '',
                })
              }
              alignSelf="end"
            >
              Clear filters
            </Button>
          </HStack>
        </Box>
      )}
    </VStack>
  );
};

export default FilterSection;
